import { Injectable } from '@angular/core';
import type { AccessTokenClaims } from '@core/models/auth.model';

const REFRESH_KEY = 'shiksha_web.rt';
const IMPERSONATION_KEY = 'shiksha_web.imp';

/**
 * Access token lives in MEMORY only (short-lived). The refresh token is
 * PERSISTED to localStorage so a full page reload can silently re-establish the
 * session (auto-login) rather than logging the user out. The session ends only
 * when the refresh token expires server-side, or on explicit logout.
 *
 * Trade-off: a persisted refresh token is readable by an XSS payload. The
 * hardened alternative is an httpOnly refresh cookie set by the backend; this
 * localStorage approach mirrors the mobile app's persisted-session behaviour.
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  // When a super_admin impersonates a school, this short-lived tenant token
  // takes precedence over the super_admin access token. It has NO refresh token
  // (it simply expires after 1h). Persisted so a reload keeps the impersonation.
  private impersonationToken: string | null = this.readImpersonation();

  setTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.persistRefresh(refreshToken);
  }

  /** The token used for API calls — impersonation token wins when active. */
  getAccessToken(): string | null {
    return this.impersonationToken ?? this.accessToken;
  }

  /** The super_admin's own access token (ignores impersonation). */
  getBaseAccessToken(): string | null {
    return this.accessToken;
  }

  // ── Impersonation ─────────────────────────────────────────────────────────────
  setImpersonationToken(token: string): void {
    this.impersonationToken = token;
    try {
      sessionStorage.setItem(IMPERSONATION_KEY, token);
    } catch {
      /* storage unavailable — impersonation just won't survive a reload */
    }
  }

  clearImpersonationToken(): void {
    this.impersonationToken = null;
    try {
      sessionStorage.removeItem(IMPERSONATION_KEY);
    } catch {
      /* storage unavailable */
    }
  }

  isImpersonating(): boolean {
    return this.impersonationToken !== null;
  }

  private readImpersonation(): string | null {
    try {
      return sessionStorage.getItem(IMPERSONATION_KEY);
    } catch {
      return null;
    }
  }

  /** In-memory refresh token, falling back to the persisted one after a reload. */
  getRefreshToken(): string | null {
    return this.refreshToken ?? this.readPersistedRefresh();
  }

  hasTokens(): boolean {
    return this.accessToken !== null;
  }

  /** Whether a persisted refresh token exists (used to attempt auto-login). */
  hasPersistedSession(): boolean {
    return this.readPersistedRefresh() !== null;
  }

  clear(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.clearImpersonationToken();
    try {
      localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* storage unavailable (private mode / SSR) — nothing to clear */
    }
  }

  private persistRefresh(refreshToken: string): void {
    try {
      localStorage.setItem(REFRESH_KEY, refreshToken);
    } catch {
      /* storage unavailable — session simply won't survive a reload */
    }
  }

  private readPersistedRefresh(): string | null {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  }

  /** Decode a JWT payload with atob (no verification — display/claims only). */
  decode(token: string | null = this.accessToken): AccessTokenClaims | null {
    if (!token) {
      return null;
    }
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    try {
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(payload)
          .split('')
          .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join(''),
      );
      return JSON.parse(json) as AccessTokenClaims;
    } catch {
      return null;
    }
  }
}
